// RaceVideoButton.js — "Download video" under the race chart: pick
// landscape (X, YouTube) or vertical (TikTok, Reels, Shorts), watch the
// progress, then save or share the MP4. The video (raceVideo.js) replays
// the race exactly as it's set up: clubs, seasons, measure, view and speed.

import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Dialog, DialogContent, IconButton, LinearProgress, Typography } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import IosShareIcon from "@mui/icons-material/IosShare";
import CloseIcon from "@mui/icons-material/Close";
import { BRAND } from "../../theme";
import { trackEvent } from "../../analytics";
import { VIDEO_FORMATS, canMakeVideo, makeRaceVideo, videoTiming } from "./raceVideo";

const duration = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;
const megabytes = (bytes) => `${(bytes / 1e6).toFixed(bytes < 1e7 ? 1 : 0)} MB`;

function save(url, name) {
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function RaceVideoButton({ race, teams, selected, colors, metric, mode, speed, range, title, fileName, onOpen }) {
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState("landscape");
  const [support, setSupport] = useState(null); // { landscape, vertical } once checked
  const [status, setStatus] = useState("idle"); // idle | making | done | error
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null); // { url, file }
  const abort = useRef(null);
  const timing = videoTiming(race, speed);
  const eyebrow = `${range} · ${metric.label}`;
  const name = `${fileName}${format === "vertical" ? "-vertical" : ""}.mp4`;

  useEffect(() => {
    if (!open || support) return;
    Promise.all([canMakeVideo("landscape"), canMakeVideo("vertical")]).then(([landscape, vertical]) => {
      setSupport({ landscape, vertical });
      if (!landscape && vertical) setFormat("vertical");
    });
  }, [open, support]);
  useEffect(() => () => result && URL.revokeObjectURL(result.url), [result]);

  const reset = () => {
    setResult(null);
    setStatus("idle");
    setProgress(0);
  };
  const cancel = () => {
    abort.current?.abort();
    reset();
  };
  const close = () => {
    abort.current?.abort();
    setOpen(false); // reset once it has faded out, so a new setup starts fresh
  };

  const make = async () => {
    const ac = new AbortController();
    abort.current = ac;
    setResult(null);
    setProgress(0);
    setStatus("making");
    const started = performance.now();
    try {
      const blob = await makeRaceVideo({ race, teams, selected, colors, metric, mode, speed, format, eyebrow, title, onProgress: setProgress, signal: ac.signal });
      const url = URL.createObjectURL(blob);
      setResult({ url, file: new File([blob], name, { type: "video/mp4" }) });
      setStatus("done");
      save(url, name);
      trackEvent("race_video", { format, video_seconds: Math.round(timing.total), clubs: selected.length, render_seconds: Math.round((performance.now() - started) / 1000) });
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.error(e);
      setStatus("error");
      trackEvent("race_video_error", { format, message: String(e?.message || e).slice(0, 100) });
    }
  };

  const canShare = result && typeof navigator !== "undefined" && navigator.canShare?.({ files: [result.file] });
  const share = () => navigator.share({ files: [result.file], title }).catch(() => null);
  const unsupported = support && !support.landscape && !support.vertical;

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={() => {
            onOpen?.();
            setOpen(true);
          }}
          sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.22)", fontWeight: 700, textTransform: "none", borderRadius: 999, px: 2, "&:hover": { borderColor: BRAND.cyan, background: "rgba(57,208,255,0.08)" } }}
        >
          Download video
        </Button>
        <Typography sx={{ color: BRAND.textSecondary, fontSize: 12.5 }}>An MP4 of this race to post on X, TikTok, Instagram or YouTube.</Typography>
      </Box>

      <Dialog
        open={open}
        onClose={close}
        fullWidth
        maxWidth="xs"
        aria-labelledby="race-video-title"
        slotProps={{
          transition: { onExited: reset },
          paper: { sx: { background: "linear-gradient(180deg, rgba(24,24,31,0.99) 0%, rgba(16,16,21,0.99) 100%)", border: `1px solid ${BRAND.divider}`, borderRadius: 3, color: "#fff" } },
        }}
      >
        <DialogContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.5 }}>
            <Typography id="race-video-title" component="h2" sx={{ fontSize: 19, fontWeight: 800, lineHeight: 1.3, flex: 1 }}>
              Download this race as a video
            </Typography>
            <IconButton onClick={close} aria-label="Close" size="small" sx={{ color: BRAND.textSecondary, mt: -0.5, mr: -1 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ p: 1.5, mb: 2, borderRadius: 2, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <Typography sx={{ color: BRAND.cyan, fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" }}>{eyebrow}</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 0.25 }}>{title}</Typography>
            <Typography sx={{ color: BRAND.textSecondary, fontSize: 12.5, mt: 0.5 }}>
              {selected.length} club{selected.length === 1 ? "" : "s"} · {speed}× speed · {duration(timing.total)} long
            </Typography>
          </Box>

          {unsupported ? (
            <Typography sx={{ color: BRAND.textSecondary, fontSize: 14, lineHeight: 1.6 }}>
              This browser can't make videos. Try the latest Chrome, Edge or Safari, on a computer or a phone.
            </Typography>
          ) : status === "making" ? (
            <>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>
                Making your video… {Math.floor(progress * 100)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress * 100}
                aria-label="Video progress"
                sx={{ height: 8, borderRadius: 4, background: "rgba(255,255,255,0.08)", "& .MuiLinearProgress-bar": { background: BRAND.gradient, borderRadius: 4 } }}
              />
              <Typography sx={{ color: BRAND.textSecondary, fontSize: 12.5, mt: 1, mb: 2 }}>It's made in your browser. Keep this tab open.</Typography>
              <Button onClick={cancel} sx={{ color: BRAND.textSecondary, textTransform: "none", fontWeight: 700 }}>
                Cancel
              </Button>
            </>
          ) : status === "done" && result ? (
            <>
              <Typography sx={{ fontSize: 15, fontWeight: 800 }} aria-live="polite">
                Your video is ready
              </Typography>
              <Typography sx={{ color: BRAND.textSecondary, fontSize: 13, mt: 0.5, mb: 2 }}>
                {VIDEO_FORMATS[format].label} · {duration(timing.total)} · {megabytes(result.file.size)}. If the download didn't start, use the button below.
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={() => save(result.url, name)}
                  sx={{ background: BRAND.gradient, fontWeight: 800, textTransform: "none", borderRadius: 999, "&:hover": { background: BRAND.gradientReverse } }}
                >
                  Download
                </Button>
                {canShare && (
                  <Button variant="outlined" startIcon={<IosShareIcon />} onClick={share} sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.22)", fontWeight: 700, textTransform: "none", borderRadius: 999 }}>
                    Share or save
                  </Button>
                )}
                <Button onClick={reset} sx={{ color: BRAND.textSecondary, fontWeight: 700, textTransform: "none" }}>
                  Make another
                </Button>
              </Box>
              <Typography sx={{ color: BRAND.textSecondary, fontSize: 12.5, mt: 2, lineHeight: 1.6 }}>
                Posting it? Tag @pl_gami and we might share it.
              </Typography>
            </>
          ) : (
            <>
              <Typography component="p" id="race-video-format" sx={{ color: BRAND.textSecondary, fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", mb: 1 }}>
                Format
              </Typography>
              <Box role="radiogroup" aria-labelledby="race-video-format" sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 2 }}>
                {Object.entries(VIDEO_FORMATS).map(([key, f]) => {
                  const ok = !support || support[key];
                  const on = format === key;
                  return (
                    <Box
                      key={key}
                      component="button"
                      type="button"
                      role="radio"
                      aria-checked={on}
                      disabled={!ok}
                      onClick={() => setFormat(key)}
                      sx={{
                        font: "inherit",
                        textAlign: "left",
                        cursor: ok ? "pointer" : "not-allowed",
                        opacity: ok ? 1 : 0.4,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        p: 1.25,
                        borderRadius: 2,
                        color: "#fff",
                        background: on ? "rgba(111,93,245,0.22)" : "rgba(255,255,255,0.03)",
                        border: `1px solid ${on ? BRAND.violet : "rgba(255,255,255,0.12)"}`,
                        "&:focus-visible": { outline: `2px solid ${BRAND.cyan}` },
                      }}
                    >
                      <Box sx={{ flexShrink: 0, width: key === "landscape" ? 30 : 17, height: key === "landscape" ? 17 : 30, border: `2px solid ${on ? BRAND.cyan : BRAND.textSecondary}`, borderRadius: 0.75 }} />
                      <Box>
                        <Box sx={{ fontSize: 13.5, fontWeight: 700 }}>{f.label}</Box>
                        <Box sx={{ fontSize: 12, color: BRAND.textSecondary, lineHeight: 1.35 }}>{f.note}</Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              {status === "error" && (
                <Typography sx={{ color: "#ff8a8a", fontSize: 13, mb: 1.5 }} role="alert">
                  Something went wrong making the video. Please try again.
                </Typography>
              )}
              <Button
                variant="contained"
                fullWidth
                disabled={!support}
                onClick={make}
                sx={{ background: BRAND.gradient, fontWeight: 800, textTransform: "none", borderRadius: 999, py: 1, "&:hover": { background: BRAND.gradientReverse } }}
              >
                {support ? "Make video" : "Checking your browser…"}
              </Button>
              <Typography sx={{ color: BRAND.textSecondary, fontSize: 12, mt: 1.5, lineHeight: 1.6 }}>
                The video plays the race as it's set up now: clubs, seasons, measure, view and speed. It's made in your browser, with
                nothing uploaded.
              </Typography>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
